import { Injectable } from '@angular/core';

import { AuthService } from '../service/auth.service';

import { Clinic } from '../clinic/clinic';
import { Patient } from '../patient/patient';
import { ExamTemplate } from '../exam-template/exam-template';

import { jsPDF } from "jspdf";


@Injectable({
  providedIn: 'root'
})
export class SadtPrint {

  readonly MULTILINE_FIELD_BORDER_HEIGHT = 21;
  readonly MULTILINE_FIELD_HEIGHT = 3.5;
  readonly MULTILINE_FIELD_HEIGHT_LIMIT = this.MULTILINE_FIELD_HEIGHT * 5;
  readonly MULTILINE_HEADING_OFFSET = this.MULTILINE_FIELD_HEIGHT_LIMIT - 1.7;

  readonly LINE_STEP = 7.5;
  readonly HEADING_HEIGHT = 3.1;
  readonly HEADING_OFFSET = 7;
  readonly MULTILINE_HEADING_HEIGHT = 2.1;

  readonly FIRST_LINE_HEIGHT = 10;
  readonly SECOND_LINE_HEIGHT = this.FIRST_LINE_HEIGHT + 9.5;
  readonly THIRD_LINE_HEIGHT = this.SECOND_LINE_HEIGHT + this.LINE_STEP;
  readonly FOURTH_LINE_HEIGHT = this.THIRD_LINE_HEIGHT + this.LINE_STEP + this.HEADING_HEIGHT;
  readonly FIFTH_LINE_HEIGHT = this.FOURTH_LINE_HEIGHT + this.LINE_STEP + this.HEADING_HEIGHT;
  readonly SIXTH_LINE_HEIGHT = this.FIFTH_LINE_HEIGHT + this.LINE_STEP;
  readonly SEVENTH_LINE_HEIGHT = this.SIXTH_LINE_HEIGHT + this.LINE_STEP + this.HEADING_HEIGHT;
  readonly EIGHTH_LINE_HEIGHT = this.SEVENTH_LINE_HEIGHT + this.LINE_STEP + this.MULTILINE_HEADING_HEIGHT;
  readonly NINETH_LINE_HEIGHT = this.EIGHTH_LINE_HEIGHT + this.MULTILINE_FIELD_HEIGHT;
  readonly TENTH_LINE_HEIGHT = this.NINETH_LINE_HEIGHT + this.HEADING_HEIGHT + this.MULTILINE_HEADING_OFFSET;
  readonly ELEVENTH_LINE_HEIGHT = this.TENTH_LINE_HEIGHT + this.LINE_STEP + this.HEADING_HEIGHT;
  readonly TWELFTH_LINE_HEIGHT = this.ELEVENTH_LINE_HEIGHT + this.LINE_STEP + this.HEADING_HEIGHT + this.MULTILINE_HEADING_HEIGHT;
  readonly THIRTEENTH_LINE_HEIGHT = this.TWELFTH_LINE_HEIGHT + this.MULTILINE_FIELD_HEIGHT;
  readonly FOURTEENTH_LINE_HEIGHT = this.THIRTEENTH_LINE_HEIGHT + this.HEADING_HEIGHT + this.MULTILINE_HEADING_HEIGHT + this.MULTILINE_HEADING_OFFSET;
  readonly FIFTEENTH_LINE_HEIGHT = this.FOURTEENTH_LINE_HEIGHT + this.MULTILINE_FIELD_HEIGHT;
  readonly SIXTEENTH_LINE_HEIGHT = this.FIFTEENTH_LINE_HEIGHT + this.HEADING_HEIGHT + this.MULTILINE_HEADING_OFFSET;
  readonly SEVENTEENTH_LINE_HEIGHT = this.SIXTEENTH_LINE_HEIGHT + this.HEADING_HEIGHT;
  readonly EIGHTEENTH_LINE_HEIGHT = this.SEVENTEENTH_LINE_HEIGHT + this.HEADING_HEIGHT + this.MULTILINE_HEADING_HEIGHT;
  readonly NINETEENTH_LINE_HEIGHT = this.EIGHTEENTH_LINE_HEIGHT + this.LINE_STEP + 4.5;
  readonly TWENTIETH_LINE_HEIGHT = this.NINETEENTH_LINE_HEIGHT + this.LINE_STEP;

  private defaultTextColor;

  constructor(private authService: AuthService) {}

  execute(patient: Patient, root_cause: string, exams: ExamTemplate[], documentNumber: number): void {

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm"
    });

    this.defaultTextColor = doc.getTextColor();

    this.printLogo(doc);
    this.printGeneralData(doc, documentNumber);
    this.printBeneficiaryData(doc, patient);
    this.printRequestorData(doc);
    this.printServiceRequestData(doc, root_cause, exams);
    this.printExecutorData(doc);
    this.printProcedureData(doc);
    this.printProcedureExecutionData(doc);
    this.printExecutingProfessionalsData(doc);
    this.printJustifications(doc);
    this.printCostFields(doc);
    this.printSignatureFields(doc);

    doc.save("sadt.pdf");

  }


  ///////////////////////////////////////////////////////////////////////////////////////
  /* REPORT PARTS */
  ///////////////////////////////////////////////////////////////////////////////////////


  private printLogo(doc: jsPDF) {
    doc.addImage("assets/image/unimed_logo.png", "PNG", 6, 6, 30, 13);
    doc.rect(5, 5, 283, 199);
  }

  private printGeneralData(doc:jsPDF, documentNumber: number) {
    this.setFontHeader(doc);
    doc.text("GUIA DE SERVIÇO PROFISSIONAL / SERVIÇO AUXILIAR DE", 142, this.FIRST_LINE_HEIGHT, null, "center");
    doc.text("DIAGNÓSTICO E TERAPIA - SP/SADT", 142, 14, null, "center");
    this.setFontSadtNumber(doc);
    doc.text(`${documentNumber}`, 260, this.FIRST_LINE_HEIGHT);

    this.setFontFieldHeader(doc);
    doc.text("2 - N", 235, this.FIRST_LINE_HEIGHT);
    doc.text("o", 239.5, this.FIRST_LINE_HEIGHT - 0.5);
    doc.text("Guia no Prestador", 241, this.FIRST_LINE_HEIGHT);

    this.printField(doc, "1 - Registro ANS", 7, this.SECOND_LINE_HEIGHT, 25);
    this.printField(doc, "3 - Número da Guia Principal", 33, this.SECOND_LINE_HEIGHT, 70);

    this.printField(doc, "4 - Data da Autorização", 7, this.THIRD_LINE_HEIGHT, 40);
    this.printField(doc, "5 - Senha", 48, this.THIRD_LINE_HEIGHT, 80);
    this.printField(doc, "6 - Data de Validade da Senha", 129, this.THIRD_LINE_HEIGHT, 40);
    this.printField(doc, "7 - Número da Guia Atribuído pela Operadora", 170, this.THIRD_LINE_HEIGHT, 80);
  }

  private printBeneficiaryData(doc: jsPDF, patient: Patient) {
    this.printHeader(doc, "Dados do Beneficiário", this.THIRD_LINE_HEIGHT + this.HEADING_OFFSET);

    this.printField(doc, "8 - Número da Carteira", 7, this.FOURTH_LINE_HEIGHT, 70);
    this.printField(doc, "9 - Validade da Carteira", 78, this.FOURTH_LINE_HEIGHT, 40);
    this.printField(doc, "10 - Nome", 119, this.FOURTH_LINE_HEIGHT, 100);
    this.fillFieldWithData(doc, patient.name, 130, this.FOURTH_LINE_HEIGHT + 2);
    this.printField(doc, "11 - Cartão Nacional de Saúde", 220, this.FOURTH_LINE_HEIGHT, 45);
    this.printField(doc, "12 - Atendimento a RN", 266, this.FOURTH_LINE_HEIGHT, 20);
 }

  private printRequestorData(doc: jsPDF) {
    this.printHeader(doc, "Dados do Solicitante", this.FOURTH_LINE_HEIGHT + this.HEADING_OFFSET);

    this.printField(doc, "13 - Código na Operadora", 7, this.FIFTH_LINE_HEIGHT, 50);
    this.printField(doc, "14 - Nome do Contratado", 58, this.FIFTH_LINE_HEIGHT, 228);

    this.printField(doc, "15 - Nome do Profissional Solicitante", 7, this.SIXTH_LINE_HEIGHT, 80);
    this.printField(doc, "16 - Conselho Profissional", 88, this.SIXTH_LINE_HEIGHT, 23);

    // doc.rect(88, this.SIXTH_LINE_HEIGHT, 15, 6.5);
    // doc.text("16 - Conselho", 89, this.SIXTH_LINE_HEIGHT + 1.5);
    // doc.text("Profissional", 92.6, this.SIXTH_LINE_HEIGHT + 2.8);

    this.printField(doc, "17 - Número no Conselho", 112, this.SIXTH_LINE_HEIGHT, 45);
    this.printField(doc, "18 - UF", 158, this.SIXTH_LINE_HEIGHT, 15);
    this.printField(doc, "19 - Código CBO", 174, this.SIXTH_LINE_HEIGHT, 30);
    this.printField(doc, "20 - Assinatura do Profissional Solicitante", 205, this.SIXTH_LINE_HEIGHT, 81);
  }

  private printServiceRequestData(doc: jsPDF, root_cause: string, exams: ExamTemplate[]) {
    this.printHeader(doc, "Dados da Solicitação / Procedimentos ou Itens Assistenciais Solicitados", this.SIXTH_LINE_HEIGHT + this.HEADING_OFFSET);

    this.printField(doc, "21 - Caráter do Atendimento", 7, this.SEVENTH_LINE_HEIGHT, 25);
    this.printField(doc, "22 - Data da Solicitação", 33, this.SEVENTH_LINE_HEIGHT, 40);
    this.printField(doc, "23 - Indicação Clínica", 74, this.SEVENTH_LINE_HEIGHT, 212);
    this.fillFieldWithData(doc, root_cause, 77, this.SEVENTH_LINE_HEIGHT + 3);

    doc.rect(7, 74, 279, this.MULTILINE_FIELD_BORDER_HEIGHT);

    doc.text("24 - Tabela", 11, this.EIGHTH_LINE_HEIGHT);
    doc.text("25 - Código do Procedimento", 30, this.EIGHTH_LINE_HEIGHT);
    doc.text("26 - Descrição", 70, this.EIGHTH_LINE_HEIGHT);
    doc.text("27 - Qtde. Solic.", 240, this.EIGHTH_LINE_HEIGHT);
    doc.text("28 - Qtde. Aut.", 260, this.EIGHTH_LINE_HEIGHT);

    let counter = 1;
    for (let i = 0; i < this.MULTILINE_FIELD_HEIGHT_LIMIT; i+=this.MULTILINE_FIELD_HEIGHT) {
      doc.text(`${counter} -`, 8, this.NINETH_LINE_HEIGHT + i);
      doc.line(11, this.NINETH_LINE_HEIGHT + i, 28, this.NINETH_LINE_HEIGHT + i);
      doc.line(30, this.NINETH_LINE_HEIGHT + i, 68, this.NINETH_LINE_HEIGHT + i);
      doc.line(70, this.NINETH_LINE_HEIGHT + i, 238, this.NINETH_LINE_HEIGHT + i);
      doc.line(240, this.NINETH_LINE_HEIGHT + i, 258, this.NINETH_LINE_HEIGHT + i);
      doc.line(260, this.NINETH_LINE_HEIGHT + i, 280, this.NINETH_LINE_HEIGHT + i);
      counter++;
    }

    for (let i = 0, j = this.NINETH_LINE_HEIGHT; i < exams.length; i += 1, j += this.MULTILINE_FIELD_HEIGHT) {
      this.fillFieldWithData(doc, exams[i].name, 73, j - 2.5);
    }
  }

  private printExecutorData(doc: jsPDF) {
    this.printHeader(doc, "Dados do Contratado Executante", this.NINETH_LINE_HEIGHT + this.MULTILINE_HEADING_OFFSET);

    this.printField(doc, "29 - Código da Operadora", 7, this.TENTH_LINE_HEIGHT, 70);
    this.printField(doc, "30 - Nome do Contratado", 78, this.TENTH_LINE_HEIGHT, 160);
    this.printField(doc, "31 - Código CNES", 239, this.TENTH_LINE_HEIGHT, 47);
  }

  private printProcedureData(doc: jsPDF) {
    this.printHeader(doc, "Dados do Atendimento", this.TENTH_LINE_HEIGHT + this.HEADING_OFFSET);

    this.printField(doc, "32 - Tipo de Atendimento", 7, this.ELEVENTH_LINE_HEIGHT, 25);
    this.printField(doc, "33 - Indicação de Acidente (acidente ou doença relacionada)", 33, this.ELEVENTH_LINE_HEIGHT, 60);
    this.printField(doc, "34 - Tipo de Consulta", 94, this.ELEVENTH_LINE_HEIGHT, 30);
    this.printField(doc, "35 - Motivo de Encerramento do Atendimento", 125, this.ELEVENTH_LINE_HEIGHT, 50);
  }

  private printProcedureExecutionData(doc: jsPDF) {
    this.printHeader(doc, "Dados da Execução / Procedimentos e Exames Realizados", this.ELEVENTH_LINE_HEIGHT + this.HEADING_OFFSET);

    doc.rect(7, this.ELEVENTH_LINE_HEIGHT + this.LINE_STEP + this.HEADING_HEIGHT, 279, this.MULTILINE_FIELD_BORDER_HEIGHT);

    doc.text("36 - Data", 11, this.TWELFTH_LINE_HEIGHT);
    doc.text("37 - Hora Inicial", 40, this.TWELFTH_LINE_HEIGHT);
    doc.text("38 - Hora Final", 55, this.TWELFTH_LINE_HEIGHT);
    doc.text("39 - Tabela", 70, this.TWELFTH_LINE_HEIGHT);
    doc.text("40 - Código Procedimento", 85, this.TWELFTH_LINE_HEIGHT);
    doc.text("41 - Descrição", 115, this.TWELFTH_LINE_HEIGHT);
    doc.text("42 - Qtde.", 175, this.TWELFTH_LINE_HEIGHT);
    doc.text("43 - Via", 190, this.TWELFTH_LINE_HEIGHT);
    doc.text("44 - Tec.", 205, this.TWELFTH_LINE_HEIGHT);
    doc.text("45 - Fator Red. / Acresc.", 215, this.TWELFTH_LINE_HEIGHT);
    doc.text("46 - Valor Unitário (R$)", 237, this.TWELFTH_LINE_HEIGHT);
    doc.text("47 - Valor Total (R$)", 259, this.TWELFTH_LINE_HEIGHT);

    let counter = 1;
    for (let i = 0; i < this.MULTILINE_FIELD_HEIGHT_LIMIT; i+=this.MULTILINE_FIELD_HEIGHT) {
      doc.text(`${counter} -`, 8, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(11, this.THIRTEENTH_LINE_HEIGHT + i, 38, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(40, this.THIRTEENTH_LINE_HEIGHT + i, 53, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(55, this.THIRTEENTH_LINE_HEIGHT + i, 68, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(70, this.THIRTEENTH_LINE_HEIGHT + i, 83, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(85, this.THIRTEENTH_LINE_HEIGHT + i, 113, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(115, this.THIRTEENTH_LINE_HEIGHT + i, 173, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(175, this.THIRTEENTH_LINE_HEIGHT + i, 188, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(190, this.THIRTEENTH_LINE_HEIGHT + i, 203, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(205, this.THIRTEENTH_LINE_HEIGHT + i, 213, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(215, this.THIRTEENTH_LINE_HEIGHT + i, 235, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(237, this.THIRTEENTH_LINE_HEIGHT + i, 257, this.THIRTEENTH_LINE_HEIGHT + i);
      doc.line(259, this.THIRTEENTH_LINE_HEIGHT + i, 280, this.THIRTEENTH_LINE_HEIGHT + i);
      counter++;
    }
  }

  private printExecutingProfessionalsData(doc: jsPDF) {
    this.printHeader(doc, "Identificação do(s) Profissional(is) Executante(s)", this.THIRTEENTH_LINE_HEIGHT + this.MULTILINE_HEADING_OFFSET);

    doc.rect(7, this.THIRTEENTH_LINE_HEIGHT + this.MULTILINE_HEADING_OFFSET + this.HEADING_HEIGHT + 0.2, 279, this.MULTILINE_FIELD_BORDER_HEIGHT);

    doc.text("48 - Seq.Ref", 8, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("49 - Grau Part.", 20, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("50 - Código na Operadora / CPF", 33, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("51 - Nome do Profissional", 100, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("52 - Conselho Profissional", 198, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("53 - Número no Conselho", 220, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("54 - UF", 255, this.FOURTEENTH_LINE_HEIGHT);
    doc.text("55 - Código CBO", 263, this.FOURTEENTH_LINE_HEIGHT);

    for (let i = 0; i < this.MULTILINE_FIELD_HEIGHT_LIMIT; i+=this.MULTILINE_FIELD_HEIGHT) {
      doc.line(8, this.FIFTEENTH_LINE_HEIGHT + i, 18, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(20, this.FIFTEENTH_LINE_HEIGHT + i, 31, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(33, this.FIFTEENTH_LINE_HEIGHT + i, 98, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(100, this.FIFTEENTH_LINE_HEIGHT + i, 196, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(198, this.FIFTEENTH_LINE_HEIGHT + i, 218, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(220, this.FIFTEENTH_LINE_HEIGHT + i, 253, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(255, this.FIFTEENTH_LINE_HEIGHT + i, 261, this.FIFTEENTH_LINE_HEIGHT + i);
      doc.line(263, this.FIFTEENTH_LINE_HEIGHT + i, 280, this.FIFTEENTH_LINE_HEIGHT + i);
    }

    doc.rect(7, this.FIFTEENTH_LINE_HEIGHT + this.MULTILINE_HEADING_OFFSET + 0.5, 279, 10);

    doc.text("56 - Data de Realização de Procedimentos em Série", 8, this.SIXTEENTH_LINE_HEIGHT);
    doc.text("57 - Assinatura do Beneficiário ou Responsável", 55, this.SIXTEENTH_LINE_HEIGHT);

    let counter = 0;
    for (let i = 0; i < 7; i+=this.MULTILINE_FIELD_HEIGHT) {
      doc.text(`${counter + 1}-`, 8, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(10, this.SEVENTEENTH_LINE_HEIGHT + i, 38, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(40, this.SEVENTEENTH_LINE_HEIGHT + i, 60, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.text(`${counter + 3}-`, 63, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(65, this.SEVENTEENTH_LINE_HEIGHT + i, 93, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(95, this.SEVENTEENTH_LINE_HEIGHT + i, 115, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.text(`${counter + 5}-`, 117, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(119, this.SEVENTEENTH_LINE_HEIGHT + i, 147, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(149, this.SEVENTEENTH_LINE_HEIGHT + i, 169, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.text(`${counter + 7}-`, 172, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(174, this.SEVENTEENTH_LINE_HEIGHT + i, 202, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(204, this.SEVENTEENTH_LINE_HEIGHT + i, 224, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.text(`${counter + 9}-`, 227, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(230, this.SEVENTEENTH_LINE_HEIGHT + i, 258, this.SEVENTEENTH_LINE_HEIGHT + i);
      doc.line(260, this.SEVENTEENTH_LINE_HEIGHT + i, 280, this.SEVENTEENTH_LINE_HEIGHT + i);
      counter++;
    }
  }

  private printJustifications(doc: jsPDF) {
    doc.setFillColor(220, 220, 220);
    doc.rect(7, this.EIGHTEENTH_LINE_HEIGHT + 0.2, 279, 11, "FD");
    doc.text("58 - Observação / Justificativa", 8, this.EIGHTEENTH_LINE_HEIGHT + this.MULTILINE_HEADING_HEIGHT);
  }

  private printCostFields(doc: jsPDF) {
    this.printField(doc, "59 - Total de Procedimentos (R$)", 7, this.NINETEENTH_LINE_HEIGHT, 38);
    this.printField(doc, "60 - Total de Taxas e Aluguéis (R$)", 46, this.NINETEENTH_LINE_HEIGHT, 38);
    this.printField(doc, "61 - Total de Materiais (R$)", 85, this.NINETEENTH_LINE_HEIGHT, 38);
    this.printField(doc, "62 - Total de OPME (R$)", 124, this.NINETEENTH_LINE_HEIGHT, 38);
    this.printField(doc, "63 - Total de Medicamentos (R$)", 163, this.NINETEENTH_LINE_HEIGHT, 38);
    this.printField(doc, "64 - Total de Gases Medicinais (R$)", 202, this.NINETEENTH_LINE_HEIGHT, 38);
    this.printField(doc, "65 - Total Geral (R$)", 241, this.NINETEENTH_LINE_HEIGHT, 45);
  }

  private printSignatureFields(doc: jsPDF) {
    this.printField(doc, "66 - Assinatura do Responsável pela Autorização", 7, this.TWENTIETH_LINE_HEIGHT, 90);
    this.printField(doc, "67 - Assinatura do Beneficiário ou Responsável", 98, this.TWENTIETH_LINE_HEIGHT, 90);
    this.printField(doc, "68 - Assinatura do Contratado", 189, this.TWENTIETH_LINE_HEIGHT, 97);
  }


  ///////////////////////////////////////////////////////////////////////////////////////
  /* HELPER FUNCTIONS */
  ///////////////////////////////////////////////////////////////////////////////////////


  private printField(doc: jsPDF, text: string, x: number, y: number, length: number) {
    doc.rect(x, y, length, 6.5);
    doc.text(text, x + 1, y + 2);
  }

  private printHeader(doc: jsPDF, text: string, y: number) {
    doc.setFillColor(220, 220, 220);
    doc.rect(7, y, 279, 3, "F");
    doc.text(text, 8, y + 2);
  }

  private setFontBold(doc: jsPDF) {
    doc.setFont("helvetica", "bold");
  }

  private setFontNormal(doc: jsPDF) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(this.defaultTextColor);
  }

  private setFontHeader(doc: jsPDF) {
    doc.setFontSize(9);
    this.setFontBold(doc);
  }

  private setFontSadtNumber(doc: jsPDF) {
    doc.setFontSize(11);
    doc.setTextColor("#b30000");
    this.setFontBold(doc);
  }

  private setFontFieldHeader(doc:jsPDF) {
    doc.setFontSize(5);
    this.setFontNormal(doc);
  }

  private fillFieldWithData(doc: jsPDF, text: string, x: number, y: number) {
    const a = doc.getFontSize();
    doc.setFontSize(10);
    doc.text(text, x + 1, y + 2);
    doc.setFontSize(a);
  }
}

